<script setup lang="ts">
import type { sources } from "@/stores/playground"
import type { MessageEventData, WorkerResponse } from "@/workers/parseWorker"
import { KTextInput } from "@kaleidoscope-solutions/ks-design-system"
import {
  FilterIcon as FilterIconFill,
  SortDownIcon,
  SortUpIcon as SortUpIconFill
} from "@kaleidoscope-solutions/ks-icons/vue/fill"
import { CloseRingIcon, FilterIcon as FilterIconLight,
  SortUpIcon as SortUpIconLight } from "@kaleidoscope-solutions/ks-icons/vue/light"
import { useClipboard, useWebWorker } from "@vueuse/core"
import { saveAs } from "file-saver"
import { computed, defineAsyncComponent, onMounted, reactive, ref, watch } from "vue"
import ArrayTable from "@/components/ArrayTable.vue"
import ColumnFilterMenu from "@/components/FormulaPlayground/ColumnFilterMenu.vue"
import CopyIcon from "@/components/FormulaPlayground/CopyIcon.svg?component"
import FormulaHistory from "@/components/FormulaPlayground/FormulaHistory.vue"
import { detectColumnType, sortRows } from "@/components/FormulaPlayground/PlaygroundUtils"
import Loading from "@/components/Loading.vue"
import TestHarness from "@/components/TestHarness/TestHarness.vue"
import { usePlaygroundStore } from "@/stores/playground"
import { DUMMY_UUID } from "@/utils"
import { useEngineModel } from "@/utils/useEngineModel"
import { EngineError } from "@/utils/useEngineModel/error"
import { useSnackbar } from "@/utils/useSnackbar"
import workerImport from "@/workers/parseWorker?worker&url"

const activeTab = ref("playground")

const FormulaEditor = defineAsyncComponent(() => import("@/components/FormulaPlayground/FormulaEditor.vue"))

const model = useEngineModel()

const worker = useWebWorker(workerImport, { type: "module" })

const loading = ref<boolean>(true)
const errorMessage = ref<string>()

const tableData = ref<string[][]>()

// Sorting and filtering state
const sortColumn = ref<number | null>(null)
const sortDirection = ref<"asc" | "desc">("asc")
const filterStates = ref<Record<number, string[]>>({})
const filterMenuOpen = ref<number | null>(null)

// Change filterIconRefs to a ref object
const filterIconRefs = reactive<Record<number, HTMLElement | null>>({})

// Compute filtered and sorted data
const filteredSortedTableData = computed(() => {
  if (!tableData.value || !tableData.value.length) {
    return tableData.value || []
  }
  let rows = tableData.value.slice(1)
  // Apply filters
  Object.entries(filterStates.value).forEach(([col, values]) => {
    if (values.length) {
      rows = rows.filter((row) => values.includes(row[+col]))
    }
  })
  // Sort
  if (sortColumn.value !== null) {
    const colType = detectColumnType([tableData.value[0], ...rows], sortColumn.value)
    rows = sortRows(rows, sortColumn.value, colType, sortDirection.value)
  }
  return [tableData.value[0], ...rows]
})

function handleSort(colIndex: number) {
  if (sortColumn.value === colIndex) {
    if (sortColumn.value === null) {
      sortColumn.value = colIndex
      sortDirection.value = "asc"
    } else if (sortDirection.value === "asc") {
      sortDirection.value = "desc"
    } else {
      sortColumn.value = null
      sortDirection.value = "asc"
    }
  } else {
    sortColumn.value = colIndex
    sortDirection.value = "asc"
  }
}

const filterMenu = computed(() => {
  if (!tableData.value || !tableData.value.length || filterMenuOpen.value === null) {
    return []
  }

  const rows = tableData.value.slice(1)
  // Only apply filters for the currently open filter menu (if any)
  const colIndex = filterMenuOpen.value
  let values = [...new Set(rows.map((row) => row[colIndex]))]
  if (sortColumn.value === colIndex) {
    const colType = detectColumnType([tableData.value[0], ...rows], colIndex)
    values = sortRows(values.map((v) => [v]), 0, colType, sortDirection.value).map((row) => row[0])
  } else {
    values.sort()
  }
  return values
})

function handleFilterIcon(colIndex: number) {
  filterMenuOpen.value = filterMenuOpen.value === colIndex ? null : colIndex
}

function updateFilter(colIndex: number, checked: string[]) {
  filterStates.value = { ...filterStates.value, [colIndex]: checked }
}

function clearFilter(colIndex: number) {
  filterStates.value = { ...filterStates.value, [colIndex]: [] }
}

function closeFilterMenu() {
  filterMenuOpen.value = null
}

const playgroundStore = usePlaygroundStore()

playgroundStore.loadState()

const partialFormula = ref("")

const text = ref<string>(structuredClone(playgroundStore.formula ?? playgroundStore.history[0] ?? ""))

const activeFormula = ref<string | null>(null)

const { addSnackbar } = useSnackbar()

function handleError(error: Error | EngineError | unknown) {
  if (error instanceof EngineError) {
    errorMessage.value = `${error.messageWithoutCode}`
  } else {
    errorMessage.value = `${error}`
  }
  tableData.value = []
  playgroundStore.resetLocalStorage()

  loading.value = false
}

async function runFormula(formula: string, source: sources) {
  if (!formula) {
    tableData.value = []
    return
  }

  filterMenuOpen.value = null
  filterStates.value = {}
  sortColumn.value = null
  sortDirection.value = "asc"

  if (source === "main") {
    partialFormula.value = ""
  }

  if (source === "selection") {
    partialFormula.value = formula
  }

  activeFormula.value = formula

  loading.value = true

  try {
    errorMessage.value = undefined

    const playground = await model.api.playground(playgroundStore.chartWidgetId || DUMMY_UUID, formula)

    const messageData: MessageEventData = {
      type: "parse",
      data: playground.data,
      childData: playground.childData,
      source,
      formula
    }

    worker.post(messageData as any)
  } catch (error) {
    handleError(error)
  }
}

watch(
  () => worker.data.value,
  () => {
    try {
      const response = worker.data.value as WorkerResponse
      if (!response) {
        return
      }

      if (!response.ok) {
        throw new Error(response.error.message)
      }

      const { formula, source } = response

      if (source && source !== "history" && formula) {
        playgroundStore.addItemToHistory(formula)
      }

      tableData.value = response.data
      loading.value = false
    } catch (error) {
      console.error(error)
      handleError(error)
    }
  },
)

function clearPartialFormula() {
  partialFormula.value = ""
  runFormula(text.value, "main")
}

function tableToCSV() {
  if (!tableData.value) {
    return null
  }

  // Export so it will auto format in Excel
  return filteredSortedTableData.value
    .map((row) =>
      row
        .map((cell) => {
          if (Array.isArray(cell)) {
            // If cell is an array, join its elements with commas and escape quotes
            const joined = cell.join(",")
            return `"${joined.replace(/"/g, "\"\"")}"`
          }
          // Otherwise, treat as string and escape quotes
          return `"${String(cell).replace(/"/g, "\"\"")}"`
        }) // Escape double quotes and handle arrays
        .join("\t") // Use tab as delimiter
    )
    .join("\n") // Join rows with newline
}

function copyTable() {
  const csvText = tableToCSV()
  if (csvText) {
    try {
      navigator.clipboard.writeText(csvText)
    } catch (error) {
      console.error(error)
      addSnackbar({
        type: "error",
        message: "Failed to copy table to clipboard",
      })
    }

    addSnackbar({
      type: "success",
      message: "Table copied to clipboard",
    })
  }
}

function saveTable() {
  const csvText = tableToCSV()
  if (csvText) {
    const blob = new Blob([csvText], { type: "text/csv;charset=utf-8" })
    const name = `${playgroundStore.chartWidgetId || "table"}.csv`
    try {
      saveAs(blob, name)
      addSnackbar({
        type: "success",
        message: `Table saved successfully as \n${name}`,
      })
    } catch (error) {
      console.error(error)
      addSnackbar({
        type: "error",
        message: "Failed to save table",
      })
    }
  }
}

function historyOptionClicked(item: string) {
  partialFormula.value = ""
  text.value = item
  runFormula(item, "history")
}

async function copyFormula() {
  if (text.value) {
    try {
      await useClipboard().copy(text.value)
    } catch (error) {
      console.error(error)
      addSnackbar({
        type: "error",
        message: "Failed to copy formula to clipboard",
      })
    }

    addSnackbar({
      type: "success",
      message: "Formula copied to clipboard",
    })
  }
}

onMounted(() => {
  if (playgroundStore.formula) {
    text.value = playgroundStore.formula
    runFormula(text.value, "main")
  } else {
    text.value = ""
  }
})

const devToolsEnabled = import.meta.env.VITE_KS_DEV_TOOLS === "true"
</script>

<template>
  <div class="flex h-full flex-col overflow-hidden p-1">
    <!-- Tab Navigation -->
    <div class="flex gap-2 border-b border-gray-200">
      <button
        class="px-4 py-2 font-medium focus:outline-none"
        :class="activeTab === 'playground' ? 'border-b-2 border-violet-600 bg-white text-violet-700' : 'text-gray-500 hover:text-violet-700'"
        type="button"
        @click="activeTab = 'playground'"
      >
        Formula Playground
      </button>
      <button
        v-if="devToolsEnabled"
        class="px-4 py-2 font-medium focus:outline-none"
        :class="activeTab === 'testHarness' ? 'border-b-2 border-violet-600 bg-white text-violet-700' : 'text-gray-500 hover:text-violet-700'"
        type="button"
        @click="activeTab = 'testHarness'"
      >
        Test Harness
      </button>
    </div>
    <div
      v-show="activeTab === 'playground'"
      class="h-full flex-1 overflow-hidden"
    >
      <div class="flex h-full flex-col gap-2 rounded-lg bg-white p-4 shadow-sm">
        <div class="ml-9 flex items-center gap-2">
          <KTextInput
            v-model="playgroundStore.chartWidgetId"
            placeholder="Chart Widget ID"
            class="font-mono"
          />
          <button
            title="Clear Chart Widget ID"
            @click="playgroundStore.chartWidgetId = null"
          >
            <CloseRingIcon class="size-7 text-violet-700" />
          </button>
        </div>
        <div class="flex pl-10">
          <span class="text-sm text-gray-700">Ctrl+Enter to run</span>
        </div>
        <div class="flex flex-col items-stretch gap-2">
          <div class="flex items-center gap-2">
            <div class="flex flex-col items-center gap-2">
              <FormulaHistory
                class="ph-no-capture"
                @option-clicked="historyOptionClicked"
              />
              <button
                title="Copy formula"
                @click="copyFormula()"
              >
                <CopyIcon class="size-7 text-violet-600" />
              </button>
            </div>
            <div class="h-full min-h-20 flex-1">
              <FormulaEditor
                class="ph-no-capture"
                :model-value="text ?? ''"
                placeholder="Formula"
                :autofocus="true"
                @update:model-value="text = $event"
                @run-formula="runFormula"
              />
            </div>

            <button
              title="Clear formula"
              @click="text = ''"
            >
              <CloseRingIcon class="size-7 text-violet-700" />
            </button>
          </div>
          <div
            v-if="partialFormula"
            class="flex items-center gap-2"
          >
            <span>Running partial formula: </span>
            <button @click="clearPartialFormula()">
              <CloseRingIcon class="size-4" />
            </button>
            <div
              class="rounded-lg bg-gray-200 p-2 font-mono"
            >
              {{ partialFormula }}
            </div>
          </div>
          <div
            v-if="errorMessage"
            class="rounded-lg bg-red-200 p-4"
          >
            {{ errorMessage }}
          </div>
        </div>
        <div
          v-if="!loading && activeFormula && !errorMessage && tableData?.length"
          :key="activeFormula + tableData.length"
          class="ph-no-capture flex-1 overflow-auto"
        >
          <div class="mb-4 flex gap-1">
            <KButton
              variety="primary"
              color="violet"
              class="shadow-none"
              @click="copyTable()"
            >
              Copy Table
            </KButton>

            <KButton
              variety="primary"
              color="violet"
              class="ms-2 shadow-none"
              @click="saveTable()"
            >
              Save Table
            </KButton>
          </div>

          <div class="mb-2 text-sm">
            Rows: {{ new Intl.NumberFormat().format(filteredSortedTableData.length - 1) }}
          </div>
          <ArrayTable
            :table-data="filteredSortedTableData"
            :read-only="false"
            :show-header="true"
          >
            <template #header-cell="{ header, colIndex }">
              <span class="font-bold">{{ header }}</span>
              <span class="flex items-center gap-1">
                <!-- Sort Icon (default) -->
                <button
                  class="inline-flex cursor-pointer flex-col select-none"
                  title="Sort"
                  @click="handleSort(colIndex)"
                >
                  <SortDownIcon
                    v-if="sortColumn === colIndex && sortDirection === 'desc'"
                    class="size-4"
                  />
                  <SortUpIconFill
                    v-else-if="sortColumn === colIndex && sortDirection === 'asc'"
                    class="size-4"
                  />
                  <SortUpIconLight
                    v-else
                    class="size-4 opacity-0 group-hover/cell:opacity-100"
                  />
                </button>
                <!-- Filter Icon -->
                <button
                  :ref="el => filterIconRefs[colIndex] = (el as HTMLElement)"
                  class="ml-1"
                  title="Filter"
                  @click.stop="handleFilterIcon(colIndex)"
                >
                  <FilterIconFill
                    v-if="filterStates[colIndex]?.length"
                    class="size-4"
                  />
                  <FilterIconLight
                    v-else
                    class="size-4 opacity-0 group-hover/cell:opacity-100"
                  />
                </button>
                <ColumnFilterMenu
                  v-if="filterMenuOpen === colIndex"
                  :values="filterMenu"
                  :checked="filterStates[colIndex] || []"
                  :anchor-el="filterIconRefs[colIndex]"
                  @update:checked="(checked: string[]) => updateFilter(colIndex, checked)"
                  @clear="() => clearFilter(colIndex)"
                  @close="closeFilterMenu"
                />
              </span>
            </template>
          </ArrayTable>
        </div>
        <div
          v-if="loading && activeFormula"
          class="fixed inset-20 z-20 grid place-content-center gap-4"
        >
          <Loading class="size-40" />
          <div class="animate-pulse">
            Running Formula ...
          </div>
        </div>
      </div>
    </div>
    <div
      v-show="activeTab === 'testHarness'"
      class="h-full flex-1 overflow-hidden"
    >
      <TestHarness />
    </div>
  </div>
</template>
